import { api } from '@packages/backend/convex/_generated/api';
import { formatCents, isWorldlinePreprod } from '@packages/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { ArrowLeft, CheckCircle2, CheckIcon, CopyIcon, XCircle } from 'lucide-react';
import { useCallback, useState } from 'react';
import { z } from 'zod';
import { SessionApplePayButton } from '@/components/payment/applePay/SessionApplePayButton';
import { SessionGooglePayButton } from '@/components/payment/googlePay/SessionGooglePayButton';
import { SessionWorldlinePaymentForm } from '@/components/payment/SessionWorldlinePaymentForm';
import { Button } from '@/components/ui/button';
import { ActionButton } from '@/components/ui+/ActionButton';
import { FullPageSpinner } from '@/components/ui+/FullPageSpinner';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { MobilePageWrapper } from '@/components/ui+/MobilePageWrapper';
import { getPaymentAmountCents } from '@/utils/payment';

const TEST_CARD_FIELDS = [
  { label: 'Card', value: '4330 2649 3634 4675' },
  { label: 'Expiry', value: '12/26' },
  { label: 'CVV', value: '123' },
] as const;

const CopyableField = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value.replace(/\s/g, ''));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[11px] text-amber-800/70">{label}</span>
      <button
        type="button"
        className="flex items-center gap-1.5 rounded px-1.5 py-0.5 font-mono text-[11px] text-amber-900 transition-colors hover:bg-amber-200/50"
        onClick={() => void handleCopy()}
      >
        {value}
        {copied ? <CheckIcon className="size-3" /> : <CopyIcon className="size-3" />}
      </button>
    </div>
  );
};

const DevTestCardInfo = () => (
  <div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
    <p className="text-xs font-medium text-amber-800">Test card</p>
    {TEST_CARD_FIELDS.map((field) => (
      <CopyableField key={field.label} {...field} />
    ))}
  </div>
);

const searchSchema = z.object({
  type: z.literal(['deposit', 'topup']).optional().default('topup'),
});

export const Route = createFileRoute('/s/$sessionUuid/payment')({
  component: Component,
  validateSearch: searchSchema,
});

function Component() {
  const { sessionUuid } = Route.useParams();
  const { type } = Route.useSearch();
  const navigate = useNavigate();
  const { data: clientConfig } = useQuery(api.app.getClientConfig);

  const isDeposit = type === 'deposit';

  const [processingStatus, setProcessingStatus] = useState<
    'idle' | 'processing' | 'success' | 'failed'
  >('idle');
  const [paymentErrorMessage, setPaymentErrorMessage] = useState<string | null>(null);
  const [showCardForm, setShowCardForm] = useState(false);

  const handlePaymentSuccess = useCallback(() => {
    setPaymentErrorMessage(null);
    setProcessingStatus('success');
    setTimeout(() => {
      void navigate({ to: '/s/$sessionUuid', params: { sessionUuid } });
    }, 1500);
  }, [navigate, sessionUuid]);

  const handlePaymentError = useCallback((message: string) => {
    setPaymentErrorMessage(message);
    setProcessingStatus('failed');
  }, []);

  const handleSubmitting = useCallback(() => {
    setPaymentErrorMessage(null);
    setProcessingStatus('processing');
  }, []);

  const handlePaymentCancel = useCallback(() => {
    setPaymentErrorMessage(null);
    setProcessingStatus('idle');
  }, []);

  if (!clientConfig) {
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <LoadingIcon />
      </div>
    );
  }

  const amountCents = getPaymentAmountCents(clientConfig.worldlineHost);

  return (
    <MobilePageWrapper>
      {processingStatus !== 'idle' && (
        <div className="flex flex-1 flex-col items-center justify-center space-y-4 p-6 text-center">
          {processingStatus === 'processing' && (
            <FullPageSpinner
              title="Processing your payment..."
              description="Please wait while we confirm your payment."
            />
          )}
          {processingStatus === 'success' && (
            <>
              <CheckCircle2 className="size-12 text-primary" />
              <p className="text-lg font-medium text-foreground">Payment successful!</p>
              <p className="text-sm text-muted-foreground">Redirecting...</p>
            </>
          )}
          {processingStatus === 'failed' && (
            <>
              <XCircle className="size-12 text-destructive" />
              <p className="text-lg font-medium text-foreground">Payment failed</p>
              <p className="text-sm text-muted-foreground">
                {paymentErrorMessage ?? 'Please try again'}
              </p>
              <ActionButton
                onClick={() => navigate({ to: '/s/$sessionUuid', params: { sessionUuid } })}
              >
                Back to session
              </ActionButton>
            </>
          )}
        </div>
      )}

      <div className={`space-y-6 ${processingStatus !== 'idle' ? 'hidden' : ''}`}>
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted"
            onClick={() => navigate({ to: '/s/$sessionUuid', params: { sessionUuid } })}
          >
            <ArrowLeft className="size-5" />
          </button>
          <div>
            <h1 className="text-lg font-semibold tracking-tight">
              {isDeposit ? 'Pay deposit' : 'Top up balance'}
            </h1>
            {isDeposit && (
              <p className="text-sm text-muted-foreground">Pay a deposit to activate your site.</p>
            )}
          </div>
        </div>

        <SessionApplePayButton
          sessionUuid={sessionUuid}
          amountCents={amountCents}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onSubmitting={handleSubmitting}
          onCancel={handlePaymentCancel}
        />

        <SessionGooglePayButton
          sessionUuid={sessionUuid}
          amountCents={amountCents}
          environment={isWorldlinePreprod(clientConfig.worldlineHost) ? 'TEST' : 'PRODUCTION'}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onSubmitting={handleSubmitting}
          onCancel={handlePaymentCancel}
        />

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground">or pay with card</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {!showCardForm && (
          <Button size="lg" className="w-full" onClick={() => setShowCardForm(true)}>
            Pay with credit card
          </Button>
        )}

        {showCardForm && (
          <>
            {isWorldlinePreprod(clientConfig.worldlineHost) && <DevTestCardInfo />}

            <SessionWorldlinePaymentForm
              sessionUuid={sessionUuid}
              amountCents={amountCents}
              worldlineHost={clientConfig.worldlineHost}
              submitLabel={`Pay ${formatCents(amountCents, '$')}`}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onSubmitting={handleSubmitting}
            />
          </>
        )}

        <p className="-mt-2 text-center text-xs text-muted-foreground">
          Any unused amount will be refunded upon checkout.
        </p>
      </div>
    </MobilePageWrapper>
  );
}
